import { request } from "./http";
import { DEFAULT_REACTION_KIND } from "../ui/reactions/reactionKinds";
import { buildReactionTogglePayload, REACTION_TARGET_TYPES, normalizeReactionResponse } from "../ui/reactions/reactionModel";
import { readStoredAuth } from "../auth/authStorage";

const BLOG_WRITE_FALLBACK_PATHS = [
    "/api/psychologist/blog/posts",
    "/api/admin/blog/posts",
];

function normalizeRole(role) {
    if (!role) return "";
    const raw =
        typeof role === "object"
            ? role?.title ?? role?.name ?? role?.role ?? role?.code ?? role?.authority ?? role?.value
            : role;
    const value = String(raw || "").trim().toUpperCase();
    return value.startsWith("ROLE_") ? value.slice(5) : value;
}

function blogWritePathsByRole() {
    const role = normalizeRole(readStoredAuth()?.me?.role);
    if (role === "ADMIN" || role === "MODERATOR") {
        return ["/api/admin/blog/posts", "/api/psychologist/blog/posts"];
    }
    if (role === "PSYCHOLOGIST") {
        return ["/api/psychologist/blog/posts"];
    }
    return BLOG_WRITE_FALLBACK_PATHS;
}

function unwrapOk(payload) {
    if (payload && typeof payload === "object") {
        if ("item" in payload && payload.item !== undefined) return payload.item;
        if ("items" in payload && payload.items !== undefined) return payload.items;
    }
    return payload;
}

function parsePositiveId(...values) {
    for (const value of values) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
}

function extractAuthorUserId(post) {
    if (!post || typeof post !== "object") return null;
    return parsePositiveId(
        post?.author?.id,
        post?.author?.userId,
        post?.author?.user_id,
        post?.authorId,
        post?.authorUserId,
        post?.userId,
        post?.user_id,
        post?.createdBy,
        post?.created_by
    );
}

function filterMineOnly(posts) {
    const me = readStoredAuth()?.me;
    const myUserId = parsePositiveId(me?.id, me?.userId, me?.user_id, me?.uid, me?.accountId, me?.account_id);
    if (!myUserId || !Array.isArray(posts)) return posts;
    return posts.filter((post) => extractAuthorUserId(post) === myUserId);
}

function canTryFallback(err) {
    const st = err?.status ?? err?.response?.status ?? null;
    return st === 403 || st === 404 || st === 405 || st === 501;
}

async function requestFirstAvailable(paths = [], makeCall) {
    let lastErr = null;
    for (const path of paths) {
        try {
            return await makeCall(path);
        } catch (err) {
            if (!canTryFallback(err)) throw err;
            lastErr = err;
        }
    }
    if (lastErr) throw lastErr;
    throw new Error("No available blog endpoint");
}

export const blogApi = {
    list: ({ page = 0, size = 12, q = "", status = "PUBLISHED", sort } = {}) => {
        const sp = new URLSearchParams();
        sp.set("page", String(page));
        sp.set("size", String(size));
        if (q) sp.set("q", q);
        if (status) sp.set("status", status);
        if (sort) sp.set("sort", sort); // например: publishedAt,desc
        return request(`/api/blog/posts?${sp.toString()}`);
    },

    getBySlug: (slug) => request(`/api/blog/posts/${encodeURIComponent(slug)}`),
    listPublished: ({ page = 0, size = 18 } = {}) =>
        request(`/api/blog/posts?page=${page}&size=${size}&status=PUBLISHED`),

    listPublishedByAuthor: ({ authorUserId, page = 0, size = 6, excludePostId } = {}) => {
        const authorId = Number(authorUserId);
        if (!Number.isFinite(authorId) || authorId <= 0) return Promise.resolve({ content: [] });
        const sp = new URLSearchParams();
        sp.set("page", String(page));
        sp.set("size", String(size));
        if (Number.isFinite(Number(excludePostId)) && Number(excludePostId) > 0) {
            sp.set("excludePostId", String(Number(excludePostId)));
        }
        return request(`/api/blog/posts/by-author/${encodeURIComponent(authorId)}?${sp.toString()}`);
    },

    getCatalog: async ({ q = "", category = "", tag = "", page = 0, size = 12, popularAuthorsLimit = 6, trendingLimit = 12 } = {}) => {
        const sp = new URLSearchParams();
        sp.set("page", String(page));
        sp.set("size", String(size));
        sp.set("popularAuthorsLimit", String(popularAuthorsLimit));
        sp.set("trendingLimit", String(trendingLimit));
        if (q) sp.set("q", q);
        if (category) sp.set("category", category);
        if (tag) sp.set("tag", tag);
        const response = await request(`/api/blog/catalog?${sp.toString()}`);
        return unwrapOk(response);
    },

    listMine: async ({ page = 0, size = 20, status = "" } = {}) => {
        const p = Number(page) || 0;
        const s = Number(size) || 20;
        const sp = new URLSearchParams();
        sp.set("page", String(p));
        sp.set("size", String(s));
        if (status) sp.set("status", String(status).toUpperCase());
        const query = sp.toString();
        const response = await requestFirstAvailable(blogWritePathsByRole().map((path) => `${path}?${query}`), (path) =>
            request(path, { method: "GET", headers: { Accept: "application/json" } })
        );
        const data = unwrapOk(response);
        if (data && typeof data === "object" && Array.isArray(data.content)) {
            const content = filterMineOnly(data.content);
            return {
                ...data,
                content,
                totalElements: content.length,
                totalPages: content.length > 0 ? 1 : 0,
            };
        }
        if (response && typeof response === "object" && Array.isArray(response.content)) {
            const content = filterMineOnly(response.content);
            return {
                ...response,
                content,
                totalElements: content.length,
                totalPages: content.length > 0 ? 1 : 0,
            };
        }
        if (Array.isArray(data)) {
            const content = filterMineOnly(data);
            return { content, number: p, size: s, totalElements: content.length, totalPages: content.length > 0 ? 1 : 0 };
        }
        return { content: [], number: p, size: s, totalElements: 0, totalPages: 0 };
    },

    getMineById: async (id) => {
        const response = await requestFirstAvailable(
            blogWritePathsByRole().map((path) => `${path}/${encodeURIComponent(id)}`),
            (path) => request(path, { method: "GET" })
        );
        return unwrapOk(response);
    },

    createForRole: async (payload = {}) => {
        const response = await requestFirstAvailable(blogWritePathsByRole(), (path) =>
            request(path, { method: "POST", json: payload })
        );
        return unwrapOk(response);
    },

    updateForRole: async (id, payload = {}) => {
        const response = await requestFirstAvailable(
            blogWritePathsByRole().map((path) => `${path}/${encodeURIComponent(id)}`),
            (path) => request(path, { method: "PUT", json: payload })
        );
        return unwrapOk(response);
    },

    deleteForRole: async (id) => {
        const response = await requestFirstAvailable(
            blogWritePathsByRole().map((path) => `${path}/${encodeURIComponent(id)}`),
            (path) => request(path, { method: "DELETE" })
        );
        return unwrapOk(response) || response || true;
    },

    listComments: async (postId) => {
        const response = await request(`/api/blog/posts/${encodeURIComponent(postId)}/comments`);
        const data = unwrapOk(response);
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data?.content)) return data.content;
        if (Array.isArray(response?.items)) return response.items;
        if (Array.isArray(response?.content)) return response.content;
        if (Array.isArray(data)) return data;
        return [];
    },

    createComment: async (postId, payload = {}) => {
        const response = await request(`/api/blog/posts/${encodeURIComponent(postId)}/comments`, {
            method: "POST",
            json: payload,
        });
        const data = unwrapOk(response);
        if (data && typeof data === "object" && data.item) return data.item;
        return data?.item ?? response?.item ?? data ?? null;
    },

    reportPost: async (postId, payload = {}) => {
        const response = await request(`/api/blog/posts/${encodeURIComponent(postId)}/report`, {
            method: "POST",
            json: payload,
        });
        return unwrapOk(response);
    },

    reportComment: async (commentId, payload = {}) => {
        const response = await request(`/api/blog/comments/${encodeURIComponent(commentId)}/report`, {
            method: "POST",
            json: payload,
        });
        return unwrapOk(response);
    },

    togglePostReaction: async (postId, reactionKind = DEFAULT_REACTION_KIND) => {
        const json = buildReactionTogglePayload({
            reaction: reactionKind,
            targetType: REACTION_TARGET_TYPES.BLOG_POST,
            targetId: postId,
        });
        const response = await request(`/api/blog/posts/${encodeURIComponent(postId)}/reactions/toggle`, {
            method: "POST",
            json,
        });
        const data = unwrapOk(response);
        return normalizeReactionResponse(data?.item ?? response?.item ?? data ?? null, reactionKind);
    },

    toggleCommentReaction: async (commentId, reactionKind = DEFAULT_REACTION_KIND) => {
        const json = buildReactionTogglePayload({
            reaction: reactionKind,
            targetType: REACTION_TARGET_TYPES.BLOG_COMMENT,
            targetId: commentId,
        });
        const response = await request(`/api/blog/comments/${encodeURIComponent(commentId)}/reactions/toggle`, {
            method: "POST",
            json,
        });
        const data = unwrapOk(response);
        return normalizeReactionResponse(data?.item ?? response?.item ?? data ?? null, reactionKind);
    },

    listAuthorSubscriptions: async () => {
        const response = await request("/api/blog/authors/subscriptions");
        const data = unwrapOk(response);
        if (Array.isArray(data)) return data;
        if (Array.isArray(response)) return response;
        return [];
    },

    toggleAuthorSubscription: async (authorUserId) => {
        const response = await request(`/api/blog/authors/${encodeURIComponent(authorUserId)}/subscribe/toggle`, {
            method: "POST",
        });
        return unwrapOk(response) || response || null;
    },
};
